/**
 * Hook for managing practitioner group memberships
 *
 * Features:
 * - Fetch all practitioner groups user belongs to (multi-group support)
 * - Invite new patients (owner only)
 * - Remove patients (owner only)
 * - Leave group (self-removal for members)
 *
 * Phase 6 Architecture:
 * - Uses practitioner_group_memberships join table (many-to-many)
 * - Supports dual enrollment (user can be in family plan + practitioner groups)
 * - RLS security: owner can see all members, members can only see own membership
 *
 * Integration:
 * - React Query via @dramadetector/api-client hooks
 * - AuthContext for user authentication
 * - CF Worker endpoints for mutations (migrated from Supabase Edge Functions in #2550)
 */
import { useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  usePractitionerGroups,
  usePractitionerInvite,
  usePractitionerRemove,
  type PractitionerGroup,
} from '@dramadetector/api-client';

export interface UsePractitionerManageReturn {
  // Groups data
  groups: PractitionerGroup[];
  isLoading: boolean;
  error: Error | null;

  // User role checks per group
  isOwner: (groupId: string) => boolean;
  isMember: (groupId: string) => boolean;

  // Summary data
  totalGroups: number;
  ownerGroups: PractitionerGroup[];
  memberGroups: PractitionerGroup[];

  // Mutations
  invitePatient: (email: string) => Promise<void>;
  removePatient: (groupId: string, userId: string) => Promise<void>;
  leaveGroup: (groupId: string) => Promise<void>;

  // Mutation states
  isInviting: boolean;
  isRemoving: boolean;
}

/**
 * Hook for managing practitioner groups the current user belongs to
 *
 * @example
 * ```typescript
 * const {
 *   groups,
 *   isLoading,
 *   ownerGroups,
 *   memberGroups,
 *   invitePatient,
 *   leaveGroup,
 * } = usePractitionerManage();
 *
 * // User can be owner of some groups and member of others
 * if (ownerGroups.length > 0) {
 *   // Show owner controls (invite, remove patients)
 * }
 *
 * if (memberGroups.length > 0) {
 *   // Show member controls (leave group)
 * }
 * ```
 */
export function usePractitionerManage(): UsePractitionerManageReturn {
  const { user } = useAuth();

  // Fetch user's practitioner groups (uses data-proxy RPC via fetch)
  const {
    data: groups = [],
    isLoading,
    error,
  } = usePractitionerGroups(user?.id);

  // Invite mutation (uses CF Worker endpoint via fetch)
  const inviteMutation = usePractitionerInvite(user?.id);

  // Remove mutation (uses CF Worker endpoint via fetch)
  const removeMutation = usePractitionerRemove(user?.id);

  // Role check helpers (stable references)
  const isOwner = useCallback(
    (groupId: string): boolean => groups.some((g) => g.id === groupId && g.role === 'owner'),
    [groups],
  );

  const isMember = useCallback(
    (groupId: string): boolean => groups.some((g) => g.id === groupId && g.role === 'member'),
    [groups],
  );

  // Separate groups by role (referentially stable)
  const ownerGroups = useMemo(() => groups.filter((g) => g.role === 'owner'), [groups]);
  const memberGroups = useMemo(() => groups.filter((g) => g.role === 'member'), [groups]);

  /**
   * Invite a patient to a practitioner group (owner only)
   *
   * The server resolves the practitioner group from the authenticated user,
   * so no groupId is needed in the request.
   */
  const invitePatient = async (email: string): Promise<void> => {
    // Verify the user owns at least one group before sending the invite
    if (ownerGroups.length === 0) {
      throw new Error('Only group owners can invite patients');
    }

    await inviteMutation.mutateAsync({
      email: email.trim().toLowerCase(),
    });
  };

  /**
   * Remove a patient from a practitioner group (owner only)
   */
  const removePatient = async (groupId: string, userId: string): Promise<void> => {
    if (!isOwner(groupId)) {
      throw new Error('Only group owners can remove patients');
    }

    await removeMutation.mutateAsync({
      practitioner_id: userId,
    });
  };

  /**
   * Leave a practitioner group (self-removal for members)
   */
  const leaveGroup = async (groupId: string): Promise<void> => {
    if (!isMember(groupId)) {
      // Owners cannot leave - they must transfer ownership or delete group
      throw new Error('Group owners cannot leave. Transfer ownership first.');
    }

    if (!user) {
      throw new Error('Not authenticated');
    }
    await removeMutation.mutateAsync({
      practitioner_id: user.id,
    });
  };

  return {
    // Groups data
    groups,
    isLoading,
    error: error as Error | null,

    // Role checks
    isOwner,
    isMember,

    // Summary data
    totalGroups: groups.length,
    ownerGroups,
    memberGroups,

    // Mutations
    invitePatient,
    removePatient,
    leaveGroup,

    // Mutation states
    isInviting: inviteMutation.isPending,
    isRemoving: removeMutation.isPending,
  };
}
